import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveApi } from '@/services/api';
import { Badge, LeaveTypeBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { FileText, CalendarX, AlertCircle, CalendarClock, Ban, Filter, Search } from 'lucide-react';

export default function MyLeavesPage() {
  const [activeTab, setActiveTab] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'>('ALL');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-leaves'],
    queryFn: async () => {
      const res = await leaveApi.getMyLeaves();
      return res.data.data;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => leaveApi.cancel(id),
    onSuccess: () => {
      toast.success('Leave application cancelled');
      queryClient.invalidateQueries({ queryKey: ['my-leaves'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'employee'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to cancel leave');
    }
  });

  const filteredLeaves = data?.filter((l: any) => activeTab === 'ALL' || l.status === activeTab) || [];

  const handleCancel = (id: string) => {
    if (confirm('Are you sure you want to cancel this leave application?')) {
      cancelMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">

      {/* Tabs */}
      <div className="bg-white p-1 rounded-xl border border-slate-200 inline-flex overflow-x-auto max-w-full scrollbar-hide">
        {['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab
                ? 'bg-slate-100 text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            {tab.charAt(0) + tab.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse"></div>)}
        </div>
      ) : filteredLeaves.length === 0 ? (
        <div className="card py-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
            <CalendarX className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700">No leaves found</h3>
          <p className="text-slate-500 max-w-sm mt-1">You don't have any leave applications matching this status.</p>
          {activeTab !== 'ALL' && (
            <Button variant="ghost" className="mt-4" onClick={() => setActiveTab('ALL')}>
              View All Leaves
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeaves.map((leave: any) => (
            <div key={leave.id} className="card p-5 hover:shadow-elevated transition-shadow group animate-fade-in">
              <div className="flex flex-col md:flex-row gap-5">
                
                {/* Left block - Dates */}
                <div className="flex items-center gap-4 md:w-64 shrink-0">
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center min-w-[80px]">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{format(new Date(leave.fromDate), 'MMM')}</p>
                    <p className="text-2xl font-black text-slate-800">{format(new Date(leave.fromDate), 'dd')}</p>
                  </div>
                  <div className="flex-1">
                    <LeaveTypeBadge colour={leave.leaveType.colour} name={leave.leaveType.name} className="mb-2" />
                    <p className="text-sm font-medium text-slate-800">
                      {leave.duration} day{leave.duration !== 1 ? 's' : ''}
                    </p>
                    {leave.fromDate !== leave.toDate && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Until {format(new Date(leave.toDate), 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Middle block - Details */}
                <div className="flex-1 border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-5">
                  <div className="flex justify-between items-start mb-2">
                    <Badge status={leave.status} />
                    <span className="text-xs text-slate-400">Applied {format(new Date(leave.appliedAt), 'MMM dd, yyyy')}</span>
                  </div>
                  <p className="text-sm text-slate-700 line-clamp-2 mt-2">{leave.reason}</p>
                  
                  {/* Approval Progress info for pending */}
                  {leave.status === 'PENDING' && leave.approvalChain?.length > 0 && (
                    <div className="mt-4 bg-amber-50/50 rounded-lg p-3 border border-amber-100/50 flex items-center gap-3">
                      <CalendarClock className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-amber-800">
                          Pending with {leave.approvalChain.find((c: any) => c.status === 'PENDING')?.approver?.name || leave.approvalChain.find((c: any) => c.status === 'PENDING')?.levelName}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Rejection comment */}
                  {leave.status === 'REJECTED' && leave.rejectionReason && (
                    <div className="mt-4 bg-red-50 rounded-lg p-3 border border-red-100 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800">{leave.rejectionReason}</p>
                    </div>
                  )}
                </div>

                {/* Right block - Actions */}
                <div className="flex md:flex-col items-center justify-end gap-2 md:w-32 shrink-0 border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-5">
                  {(leave.status === 'PENDING' || (leave.status === 'APPROVED' && new Date(leave.fromDate) > new Date())) && (
                    <Button
                      variant="danger"
                      size="sm"
                      fullWidth
                      onClick={() => handleCancel(leave.id)}
                      isLoading={cancelMutation.isPending}
                    >
                      Cancel
                    </Button>
                  )}
                  {leave.documentUrl && (
                    <Button variant="secondary" size="sm" fullWidth>
                      <FileText className="w-4 h-4 mr-2" /> View Doc
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
